import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";
// helpers
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { getSummaryRouteTitle } from "../helpers/getSummaryRouteTitle";
// actions
import { limos4ActionCreators } from "../limos4Module";
// hooks
import { useActions } from "../../../../common/hooks/useActions";

interface Limos4SummaryTypes {
  onBookBtn: () => void;
  onEditTicket: () => void;
  selectedFromStation: any;
  selectedToStation: any;
  cancellationPolicy?: string | null;
}

export default function Limos4Summary({
  onBookBtn,
  onEditTicket,
  selectedFromStation,
  selectedToStation,
  cancellationPolicy
}: Limos4SummaryTypes) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";
  const [checkPhoneValidation, setCheckPhoneValidation] = useState(false);

  // states
  const platformType = useSelector((state: any) => state.global.platformType);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const dataForBooking = useSelector((state: any) => state.limos4.dataForBooking);
  const limos4SelectedOffer = useSelector((state: any) => state.limos4.limos4SelectedOffer);
  const limos4Estimation = useSelector((state: any) => state.limos4.limos4Estimation);
  const errorField = useSelector((state: any) => state.errorField.errorField);
  const limos4BookingError = useSelector((state: any) => state.limos4.bookingError);
  const limos4BookingErrorMessage = useSelector((state: any) => state.limos4.bookingErrorMessage);

  // actions
  const updateFormForBooking = useActions(limos4ActionCreators?.updateFormForBookingAction, []);
  const toggleLimos4BookingError = useActions(limos4ActionCreators?.toggleBookingErrorAction, []);

  const isAirline = platformType === "airline";

  useEffect(() => {
    if (limos4BookingErrorMessage) {
      const hasPhoneField = limos4BookingErrorMessage.some((f: any) => f.field === "phone");
      setCheckPhoneValidation(hasPhoneField);
    }
  }, [limos4BookingErrorMessage]);

  const handleBookingFormInputChange = (e: any, input: any) => {
    switch (input) {
      case "nameForm":
        updateFormForBooking({
          ...dataForBooking,
          nameForm: e.target.value
        });
        break;
      case "emailForm":
        updateFormForBooking({
          ...dataForBooking,
          emailForm: e.target.value
        });
        break;
      case "mobileNumberForm":
        updateFormForBooking({
          ...dataForBooking,
          mobileNumberForm: e.target.value
        });
        break;
      case "commentForm":
        updateFormForBooking({
          ...dataForBooking,
          commentForm: e.target.value
        });
        break;
      default:
        break;
    }
  };

  const renderBookErrorMsg = () => {
    if (!limos4BookingErrorMessage || limos4BookingErrorMessage?.length === 0) {
      return (
        <Box mt={1} className="limos4__error-msg">
          {t("transport.prebookCustomError")}
        </Box>
      );
    }

    return limos4BookingErrorMessage?.map((error: any, i: number) => (
      <Box mt={1} className="limos4__error-msg" key={error?.field ? `${error.field}-${i}` : `limos4-error-${i}`}>
        {error?.errorMessage ? `${error.errorMessage}` : t("transport.prebookCustomError")}
      </Box>
    ));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
          <Box>
            <Box className="summary-option-title" mb={2}>
              {t("transport.ticketOption")}
            </Box>
            <Box className="summary-option-title default-text-transform">
              {getSummaryRouteTitle(
                t,
                selectedFromStation?.value?.structured_formatting?.main_text,
                selectedToStation?.value?.structured_formatting?.main_text
              )}
            </Box>
          </Box>
          <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
            <AppButton
              label={t("transport.editBtn")}
              onClick={() => {
                if (window.confirm(t("transport.editOptionText"))) {
                  onEditTicket();
                  toggleLimos4BookingError(false);
                }
              }}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
              dataAttribute="limos4-edit"
            />
          </Grid>
        </Box>

        <Box sx={{ display: "flex", margin: "30px 0 15px 0 " }}>
          <Box sx={{ display: "flex", alignItems: "center" }} />
          <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
            {t("transport.totalPrice")}: {limos4Estimation?.formatted_price}
          </Box>
        </Box>
      </Box>

      {/* BOOKING SECTION */}
      <Grid container mt={2}>
        {/* Left side: booking form */}
        <Grid item xs={12} md={6}>
          <Grid container width={{ sm: "100%", md: "100%" }}>
            {limos4SelectedOffer?.booking_setting?.customer_name !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.guestNameLabel")}{" "}
                    {limos4SelectedOffer?.booking_setting?.customer_name === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"nameForm"}
                    type="string"
                    value={dataForBooking?.nameForm}
                    onChange={event => handleBookingFormInputChange(event, "nameForm")}
                    error={errorField?.nameForm}
                    dataAttribute="limos4-name"
                  />
                </Grid>
              </Grid>
            )}
            {limos4SelectedOffer?.booking_setting?.customer_email !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.guestEmailLabel")}{" "}
                    {limos4SelectedOffer?.booking_setting?.customer_email === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"emailForm"}
                    type="string"
                    value={dataForBooking?.emailForm}
                    onChange={event => handleBookingFormInputChange(event, "emailForm")}
                    error={errorField?.emailForm}
                    dataAttribute="limos4-email"
                  />
                </Grid>
              </Grid>
            )}
            {limos4SelectedOffer?.booking_setting?.customer_phone !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.guestPhoneLabel")}{" "}
                    {limos4SelectedOffer?.booking_setting?.customer_phone === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"mobileNumberForm"}
                    type="string"
                    value={dataForBooking?.mobileNumberForm}
                    onChange={event => handleBookingFormInputChange(event, "mobileNumberForm")}
                    error={errorField?.mobileNumberForm || checkPhoneValidation}
                    dataAttribute="limos4-phone"
                  />
                  <Box sx={{ fontSize: { xs: 10, sm: 12 }, mt: 1 }}>{t("phoneNumberLabel")}</Box>
                </Grid>
              </Grid>
            )}
            {limos4SelectedOffer?.booking_setting?.comment !== "hidden" && (
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                    {t("transport.commentFlightNoLabel")}{" "}
                    {limos4SelectedOffer?.booking_setting?.comment === "mandatory" && "*"}
                  </Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"commentForm"}
                    type="string"
                    value={dataForBooking?.commentForm}
                    onChange={event => handleBookingFormInputChange(event, "commentForm")}
                    error={errorField?.commentForm}
                    dataAttribute="limos4-comment"
                  />
                  <Box sx={{ fontSize: { xs: 10, sm: 12 }, mt: 1 }}>{t("transport.commentFlightNoHint")}</Box>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Right side: Miles & More info (responsive: below on mobile) */}
        {shouldShowMilesAndMore(limos4Estimation?.miles_and_more, isAirline) && (
          <Grid
            item
            xs={12}
            md={6}
            pl={{ xs: 0, md: 5 }}
            pt={{ xs: 4, md: 0 }}
            pb={{ xs: 0, md: 6 }}
            sx={{ alignItems: "center", display: "flex" }}
          >
            <MilesAndMoreInfo miles={limos4Estimation?.miles_and_more} />
          </Grid>
        )}
      </Grid>

      {limos4BookingError && renderBookErrorMsg()}

      {cancellationPolicy && (
        <Box pt={6}>
          <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
        </Box>
      )}

      <Grid
        item
        xs={12}
        sx={{
          width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
          margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "25px auto 0 auto",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 2 }
        }}
      >
        {isAirline && (
          <AppButton
            label={t("transport.goBack")}
            onClick={() => navigateBackOrToMain(navigate, { language })}
            disableRipple={true}
            customBackgroundColor={theme.palette.primary.dark}
            customHoverColor={theme.palette.primary.dark}
            fontSize="14px"
            dataAttribute="limos4-back"
          />
        )}
        <AppButton
          label={t("transport.bookButton")}
          onClick={onBookBtn}
          disableRipple={true}
          customBackgroundColor={tenantInfo?.style?.button?.color}
          customActiveColor={tenantInfo?.style?.button?.active}
          customHoverColor={tenantInfo?.style?.button?.hover}
          customClickColor={tenantInfo?.style?.button?.click}
          fontSize="14px"
          disabled={false}
          dataAttribute="limos4-book"
        />
      </Grid>
    </Box>
  );
}
